/**
 * 진행 상황 표시를 관리하는 Provider입니다.
 *
 * 주요 기능:
 * - 진행률 표시 (showProgress, updateProgress, hideProgress)
 * - 작업 취소 지원 (requestCancel, AbortController)
 *
 * @remarks
 * 일괄 주문, 데이터 로딩 등 장시간 작업의 진행 상황을 사용자에게 표시합니다.
 * AbortController를 통해 진행 중인 Promise를 취소할 수 있습니다.
 */
import type { ReactNode } from 'react';
import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

/** 진행 상황 상태 */
export interface ProgressState {
  visible: boolean;
  message: string;
  percent: number;
  total: number;
  completed: number;
  isCanceling: boolean;
}

interface ProgressContextValue {
  /** 현재 진행 상태 */
  progress: ProgressState;
  /** 진행 상황 표시 시작 */
  showProgress: (message: string, total: number) => void;
  /** 진행 상황 업데이트 */
  updateProgress: (completed: number, total: number, message?: string) => void;
  /** 진행 상황 숨기기 */
  hideProgress: () => void;
  /** 취소 요청 여부 - useRef로 최신 값을 참조 */
  isCancelRequestedRef: React.MutableRefObject<boolean>;
  /** 취소 요청 */
  requestCancel: () => void;
  /** AbortController ref - pending Promise 취소용 */
  abortControllerRef: React.MutableRefObject<AbortController | null>;
}

const ProgressContext = createContext<ProgressContextValue | null>(null);

/**
 * 진행 상황 표시를 관리하는 Provider.
 * 일괄 주문, 데이터 로딩 등 장시간 작업의 진행 상황을 사용자에게 표시합니다.
 * AbortController를 통해 진행 중인 Promise를 취소할 수 있습니다.
 */
export const ProgressProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [progress, setProgress] = useState<ProgressState>({
    visible: false,
    message: '',
    percent: 0,
    total: 0,
    completed: 0,
    isCanceling: false,
  });
  const isCancelRequestedRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const showProgress = useCallback((message: string, total: number) => {
    // 기존 AbortController가 있으면 abort 후 새로 생성
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();

    setProgress({
      visible: true,
      message,
      percent: 0,
      total,
      completed: 0,
      isCanceling: false,
    });
    isCancelRequestedRef.current = false;
  }, []);

  const updateProgress = useCallback((completed: number, total: number, message?: string) => {
    const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
    setProgress(prev => ({
      ...prev,
      completed,
      total,
      percent,
      message: message || prev.message,
    }));
  }, []);

  const hideProgress = useCallback(() => {
    setProgress(prev => ({
      ...prev,
      visible: false,
    }));
    isCancelRequestedRef.current = false;
    // hideProgress 시 AbortController 정리
    abortControllerRef.current = null;
  }, []);

  const requestCancel = useCallback(() => {
    setProgress(prev => ({
      ...prev,
      isCanceling: true,
    }));
    isCancelRequestedRef.current = true;
    // AbortController를 통해 pending Promise들에 abort signal 전송
    abortControllerRef.current?.abort();
  }, []);

  const contextValue: ProgressContextValue = {
    progress,
    showProgress,
    updateProgress,
    hideProgress,
    isCancelRequestedRef,
    requestCancel,
    abortControllerRef,
  };

  return <ProgressContext.Provider value={contextValue}>{children}</ProgressContext.Provider>;
};

/**
 * 진행 상황 컨텍스트 훅. 진행 상태와 제어 함수(showProgress, updateProgress, hideProgress, requestCancel)를 제공합니다.
 * @throws ProgressProvider 외부에서 사용 시 에러를 발생시킵니다.
 */
export const useProgress = (): ProgressContextValue => {
  const context = useContext(ProgressContext);
  if (!context) {
    throw new Error('useProgress must be used within ProgressProvider');
  }
  return context;
};
